import { Add } from '@mui/icons-material'
import Container from '../../../components/Container'
import { Grid2, IconButton, Skeleton, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import { Outlet, useNavigate } from 'react-router-dom'
import { useEffect, useMemo, useState } from 'react'
import { IManager, IUser } from 'sierra'
import api from '../../../utils/axios'
import UserCardSkeleton from '../../../components/Skeletons/UserCardSkeleton'
import UserCard from '../../../components/Cards/UserCard'
import { useApp } from '../../../contexts/AppContext'
import { useAuth } from '../../../hooks/useAuth'

type TTabs = 'all' | 'manager' | 'employee' | 'customer'

export default function SystemUsers() {
    const { user } = useAuth()
    const navigate = useNavigate()
    const { translate } = useApp()

    const [users, setUsers] = useState<IUser[]>()
    const [search, setSearch] = useState('')
    const [tab, setTab] = useState<TTabs>('all')
    const [manager, setManager] = useState<Required<IManager> | null>(null)

    const filteredUsers = useMemo(() => {
        if (!users) return undefined;
        return users.filter(u =>
            (tab === 'all' ? true : u.role === tab) &&
            (!search ? true : u.name.toLowerCase().match(search.toLowerCase()) || u.email.toLowerCase().match(search.toLowerCase()))
        )
    }, [users, tab, search])

    useEffect(() => {
        fetchUsers()
    }, [])

    useEffect(() => {
        if (!user || user.role !== 'manager') return
        fetchManager()
    }, [user])

    useEffect(() => {
        if (user && user.role === 'manager') setTab('employee')
    }, [user])

    const fetchUsers = () => {
        api.get('/users').then(res => setUsers(res.data.filter((u: IUser) => !u.is_deleted))).catch(console.error)
    }

    const fetchManager = () => {
        api.get('/managers', { params: { user_id: user?.user_id } }).then(res => setManager(res.data[0])).catch(console.error)
    }

    return (
        <Grid2 container size={'grow'} spacing={location.pathname.split('/')[3] ? 2 : 0} sx={{ transition: { xs: 'none', lg: location.pathname.split('/')[3] ? 'all 0.8s cubic-bezier(0, 0.5, 0.2, 1)' : 'all 0.5s cubic-bezier(0.6, 0, 0.8, 0.4)' } }}>

            <Grid2 container display={{ xs: location.pathname.split('/')[3] ? 'none' : 'flex', lg: 'flex' }} size={{ xs: location.pathname.split('/')[3] ? 0 : 'grow', lg: 'grow' }} flexDirection={'column'} spacing={2}>

                <Container container size={'auto'} padding={2} justifyContent={'space-between'} alignItems={'center'}>
                    <Grid2>
                        <Typography variant='h4' fontWeight={'bold'} color='primary'>
                            {translate("Users")}
                        </Typography>
                    </Grid2>
                    <Grid2>
                        <IconButton onClick={() => navigate('invite')}>
                            <Add />
                        </IconButton>
                    </Grid2>
                </Container>

                <Container container width={'100%'} flexDirection={{ xs: 'column', lg: 'row' }} padding={2} spacing={2} size='auto' alignItems={'center'} justifyContent={'space-between'} wrap='nowrap'>
                    <Grid2 size='grow'>
                        {
                            !user ? <Stack gap={2} direction={'row'}>{Array.from({ length: 3 }).map(() => <Skeleton width={150} height={50} variant='rounded' />)}</Stack> :
                                <Tabs variant='scrollable' value={tab} onChange={(_, v) => setTab(v)}>
                                    {user.role === 'admin' && <Tab label={translate("All")} value={'all'} />}
                                    {user.role === 'admin' && <Tab label={translate("Managers")} value={'manager'} />}
                                    <Tab label={translate("Employees")} value={'employee'} />
                                    {user.role === 'admin' && <Tab label={translate("Customers")} value={'customer'} />}
                                </Tabs>
                        }
                    </Grid2>
                    <Grid2 width={{ xs: '100%', sm: 'auto' }}>
                        <TextField fullWidth value={search} onChange={(e) => setSearch(e.target.value)} variant='filled' size='small' label={translate('Search')} />
                    </Grid2>
                </Container>

                <Container container flexDirection={'column'} spacing={2} padding={2}>
                    <Grid2 container size='grow' flexDirection={'column'}>
                        <Grid2 container flexDirection={'column'} gap={2}>
                            {!filteredUsers ? <Grid2><Stack direction='column' gap={2}>{Array.from({ length: 3 }).map((_, i) => <UserCardSkeleton key={i} />)}</Stack></Grid2> :
                                filteredUsers.map((user) => (
                                    <Grid2 key={user.user_id}>
                                        <UserCard user={user} onRefresh={fetchUsers} manager={manager ? manager : undefined} />
                                    </Grid2>
                                ))}
                        </Grid2>
                    </Grid2>
                </Container>

            </Grid2>

            <Container size={location.pathname.split('/')[3] ? 'grow' : 0} flexGrow={location.pathname.split('/')[3] ? 1 : 0} sx={{ transition: { xs: 'none', lg: 'all 0.5s ease' } }}>
                <Outlet />
            </Container>

        </Grid2>
    )
}